import { select, Selection } from 'd3-selection'
import { Transition } from 'd3-transition'
import { arc } from 'd3-shape'

// Types
import { GraphInputLink, GraphInputNode } from '@/types/graph'
import { FitMode, TrimMode, UnovisText } from '@/types/text'
import { BooleanAccessor, GenericAccessor, NumericAccessor, StringAccessor } from '@/types/accessor'

// Utils
import { renderTextToSvgTextElement, trimString } from '@/utils/text'
import { polygon } from '@/utils/path'
import { smartTransition, Selection$Transition } from '@/utils/d3'
import { getBoolean, getNumber, getString, getValue, throttle } from '@/utils/data'
import { getColor } from '@/utils/color'
import { isStringSvg } from '@/utils/svg'
import { getCSSVariableValueInPixels } from '@/utils/misc'

// Local Types
import { GraphNode, GraphCircleLabel, GraphNodeAnimationState, GraphNodeAnimatedElement, GraphNodeShape } from '../../types'

// Config
import { GraphConfigInterface } from '../../config'

// Helpers
import {
  arcTween,
  polyTween,
  setLabelRect,
  getX,
  getY,
  getSideLabelTextColor,
  getNodeColor,
  getNodeIconColor,
  getNodeSize,
  LABEL_RECT_VERTICAL_PADDING,
  isInternalHref,
} from './helper'
import { appendShape, updateShape } from '../shape'
import { ZoomLevel } from '../zoom-levels'

// Styles
import * as generalSelectors from '../../style'
import * as nodeSelectors from './style'

const SIDE_LABEL_DEFAULT_RADIUS = 10

export interface GraphNodeSVGGElement extends SVGGElement {
  nodeShape?: string;
  nodeIcon?: string;
}

export function createNodes<N extends GraphInputNode, L extends GraphInputLink> (
  selection: Selection<SVGGElement, GraphNode<N, L>, SVGGElement, unknown>,
  config: GraphConfigInterface<N, L>,
  duration: number,
  scale = 1
): void {
  selection.each((d, i, elements) => {
    const element = elements[i] as GraphNodeSVGGElement
    const group = select<SVGGElement, GraphNode<N, L>>(element)
    group
      .attr('transform', (d: GraphNode<N, L>, i) => {
        const configuredPosition = getValue<GraphNode<N, L>, [number, number] | undefined>(d, config.nodeEnterPosition, i)
        const scale = getNumber(d, config.nodeEnterScale, i) ?? 0
        const x = configuredPosition?.[0] ?? getX(d)
        const y = configuredPosition?.[1] ?? getY(d)
        return `translate(${x}, ${y}) scale(${scale})`
      })
      .attr('opacity', 0)

    // If there's a custom render function, use it
    if (config.nodeEnterCustomRenderFunction) {
      config.nodeEnterCustomRenderFunction(d, group, config, duration, scale)
    } else { // Default node rendering
      const shape = getString(d, config.nodeShape, d._index) as GraphNodeShape
      // Todo: The 'nodeShape'  and `nodeIcon` storing logic below it a temporary solution,
      // we need a cleaner implementation
      element.nodeShape = shape
      appendShape(group, shape, nodeSelectors.node, nodeSelectors.customNode, d._index)
      appendShape(group, shape, nodeSelectors.nodeSelection, nodeSelectors.customNode, d._index)
      group.append('path').attr('class', nodeSelectors.nodeGauge)
      group.append('g').attr('class', nodeSelectors.nodeIcon)

      group.append('g')
        .attr('class', nodeSelectors.sideLabelsGroup)

      group.append('text')
        .attr('class', nodeSelectors.nodeBottomIcon)
    }

    // Label. The label and sub-label are rendered into the `<text>` element
    // as text blocks with the `nodeSelectors.labelTextContent` and
    // `nodeSelectors.subLabelTextContent` classes, see `renderNodeLabels`
    const label = group.append('g').attr('class', nodeSelectors.label)
    label.append('rect').attr('class', nodeSelectors.labelBackground)
    label.append('text')
      .attr('class', nodeSelectors.labelText)
  })
}

/** Updates the nodes partially according to their `_state` */
export function updateNodesPartial<N extends GraphInputNode, L extends GraphInputLink> (
  selection: Selection<SVGGElement, GraphNode<N, L>, SVGGElement, unknown>,
  config: GraphConfigInterface<N, L>,
  duration: number,
  scale = 1
): void {
  const { nodeDisabled } = config

  if (config.nodePartialUpdateCustomRenderFunction || config.nodeEnterCustomRenderFunction) {
    // Handle custom rendering behavior
    selection.each((d, i, elements) => {
      const g = select<SVGGElement, GraphNode<N, L>>(elements[i])
      config.nodePartialUpdateCustomRenderFunction?.(d, g, config, duration, scale)
    })
  } else {
    // Default rendering
    selection.each((d, i, elements) => {
      const group: Selection<SVGGElement, GraphNode<N, L>, SVGGElement, unknown> = select(elements[i])
      const isGreyout = getBoolean(d, nodeDisabled, d._index) || d._state.greyout

      group.classed(nodeSelectors.greyedOutNode, isGreyout && !d._state.brushed)
        .classed(nodeSelectors.draggable, !config.disableDrag)

      const nodeSelectionOutline = group.selectAll<SVGGElement, GraphNode<N, L>>(`.${nodeSelectors.nodeSelection}`)
      nodeSelectionOutline.classed(nodeSelectors.nodeSelectionActive, d._state.selected || d._state.brushed)

      group.selectAll<SVGTextElement, GraphCircleLabel>(`.${nodeSelectors.sideLabel}`)
        .style('fill', (l) => isGreyout ? null : getSideLabelTextColor(l, selection.node()))

      group.selectAll<SVGRectElement, GraphCircleLabel>(`.${nodeSelectors.sideLabelBackground}`)
        .style('fill', (l) => isGreyout ? null : l.color)
    })
  }
}

export function updateNodePositions<N extends GraphInputNode, L extends GraphInputLink> (
  selection: Selection<SVGGElement, GraphNode<N, L>, SVGGElement, unknown>,
  duration: number
): Selection$Transition<SVGGElement, GraphNode<N, L>, SVGGElement, unknown> {
  return smartTransition(selection, duration)
    .attr('transform', d => `translate(${getX(d)}, ${getY(d)}) scale(1)`)
    .attr('opacity', 1)
}

/** Resolves the visible text and wrapping settings of a node label or sub-label into an UnovisText block */
function getNodeLabelTextBlock<N extends GraphInputNode, L extends GraphInputLink> (
  d: GraphNode<N, L>,
  accessors: {
    text: StringAccessor<N>;
    fitMode: GenericAccessor<FitMode | string, N>;
    trim: BooleanAccessor<N>;
    trimMode: GenericAccessor<TrimMode | string, N>;
    trimLength: NumericAccessor<N>;
    width: NumericAccessor<N>;
    separator: string | string[] | undefined;
    forceWordBreak: boolean | undefined;
  },
  fontSize: number,
  className: string,
  defaultWrapWidth: number,
  forceExpanded: boolean
): UnovisText | undefined {
  const text = getString(d, accessors.text, d._index)
  if (!text) return undefined

  const shouldWrap = getValue(d, accessors.fitMode, d._index) === FitMode.Wrap
  const visibleText = (!shouldWrap && !forceExpanded && getBoolean(d, accessors.trim, d._index))
    ? trimString(text, getNumber(d, accessors.trimLength, d._index), getValue(d, accessors.trimMode as TrimMode, d._index))
    : text

  return {
    text: visibleText,
    fontSize,
    className,
    width: shouldWrap ? (getNumber(d, accessors.width, d._index) ?? defaultWrapWidth) : undefined,
    separator: accessors.separator,
    wordBreak: accessors.forceWordBreak,
  }
}

/** Renders the node label and sub-label as text blocks into the label's `<text>` element */
function renderNodeLabels<N extends GraphInputNode, L extends GraphInputLink> (
  label: Selection<SVGGElement, GraphNode<N, L>, SVGGElement, unknown>,
  d: GraphNode<N, L>,
  config: GraphConfigInterface<N, L>,
  labelFontSize: number,
  subLabelFontSize: number,
  forceExpanded = false
): void {
  const defaultWrapWidth = Math.max(getNodeSize(d, config.nodeSize, d._index) * 2.5, labelFontSize * 4)
  const blocks = [
    getNodeLabelTextBlock(d, {
      text: config.nodeLabel,
      fitMode: config.nodeLabelFitMode,
      trim: config.nodeLabelTrim,
      trimMode: config.nodeLabelTrimMode,
      trimLength: config.nodeLabelTrimLength,
      width: config.nodeLabelWidth,
      separator: config.nodeLabelSeparator,
      forceWordBreak: config.nodeLabelForceWordBreak,
    }, labelFontSize, nodeSelectors.labelTextContent, defaultWrapWidth, forceExpanded),
    getNodeLabelTextBlock(d, {
      text: config.nodeSubLabel,
      fitMode: config.nodeSubLabelFitMode,
      trim: config.nodeSubLabelTrim,
      trimMode: config.nodeSubLabelTrimMode,
      trimLength: config.nodeSubLabelTrimLength,
      width: config.nodeSubLabelWidth,
      separator: config.nodeSubLabelSeparator,
      forceWordBreak: config.nodeSubLabelForceWordBreak,
    }, subLabelFontSize, nodeSelectors.subLabelTextContent, defaultWrapWidth, forceExpanded),
  ].filter(Boolean) as UnovisText[]

  const labelTextSelection = label.select<SVGTextElement>(`.${nodeSelectors.labelText}`)
  if (blocks.length) renderTextToSvgTextElement(labelTextSelection.node(), blocks, {})
  else labelTextSelection.text('')
}

export function updateNodes<N extends GraphInputNode, L extends GraphInputLink> (
  selection: Selection<SVGGElement, GraphNode<N, L>, SVGGElement, unknown>,
  config: GraphConfigInterface<N, L>,
  duration: number,
  scale = 1
): Selection<SVGGElement, GraphNode<N, L>, SVGGElement, unknown> | Transition<SVGGElement, GraphNode<N, L>, SVGGElement, unknown> {
  const {
    nodeGaugeAnimDuration, nodeStrokeWidth, nodeShape, nodeSize, nodeGaugeValue, nodeGaugeFill,
    nodeIcon, nodeIconSize,
    nodeSideLabels, nodeStroke, nodeFill, nodeBottomIcon,
  } = config

  const nodeGroupsUpdate = updateNodePositions(selection, duration)

  // If there's a custom render function, use it
  if (config.nodeUpdateCustomRenderFunction) {
    selection.each((d, i, elements) => {
      const g = select<SVGGElement, GraphNode<N, L>>(elements[i])
      config.nodeUpdateCustomRenderFunction(d, g, config, duration, scale)
    })

    updateNodesPartial(selection, config, duration, scale)
    return nodeGroupsUpdate
  }

  // Default node rendering
  // Re-create nodes to update shapes if they were changed
  selection.each((d, i, elements) => {
    const element = elements[i] as GraphNodeSVGGElement
    const group = select<SVGGElement, GraphNode<N, L>>(element)
    const shape = getString(d, nodeShape, d._index)

    if (element.nodeShape !== shape) {
      group.select(`.${nodeSelectors.node}`).remove()
      appendShape(group, nodeShape, nodeSelectors.node, nodeSelectors.customNode, d._index, `.${nodeSelectors.nodeSelection}`)
      group.select(`.${nodeSelectors.nodeSelection}`).remove()
      appendShape(group, shape, nodeSelectors.nodeSelection, null, d._index, `.${nodeSelectors.nodeGauge}`)
      element.nodeShape = shape
    }
  })

  // Update nodes themselves
  selection.each((d, i, elements) => {
    const groupElement = elements[i] as GraphNodeSVGGElement
    const group: Selection<SVGGElement, GraphNode<N, L>, SVGGElement, unknown> = select(groupElement)
    const node: Selection<SVGGElement, GraphNode<N, L>, SVGGElement, unknown> = group.select(`.${nodeSelectors.node}`)
    const nodeArc = group.select<GraphNodeAnimatedElement<SVGElement>>(`.${nodeSelectors.nodeGauge}`)
    const icon = group.select<SVGTextElement>(`.${nodeSelectors.nodeIcon}`)
    const sideLabelsGroup = group.select<SVGGElement>(`.${nodeSelectors.sideLabelsGroup}`)
    const label = group.select<SVGGElement>(`.${nodeSelectors.label}`)
    const bottomIcon = group.select<SVGTextElement>(`.${nodeSelectors.nodeBottomIcon}`)
    const nodeSelectionOutline = group.select<SVGGElement>(`.${nodeSelectors.nodeSelection}`)
    const nodeSizeValue = getNodeSize(d, nodeSize, d._index)
    const arcGenerator = arc<GraphNodeAnimationState>()
      .innerRadius(state => state.nodeSize / 2 - state.borderWidth / 2)
      .outerRadius(state => state.nodeSize / 2 + state.borderWidth / 2)
      .startAngle(0 * (Math.PI / 180))
      // eslint-disable-next-line dot-notation
      .endAngle(a => a['endAngle'])

    group
      .classed(generalSelectors.zoomOutLevel2, scale < ZoomLevel.Level2)
      .classed(nodeSelectors.nodeIsDragged, (d: GraphNode<N, L>) => d._state.isDragged)

    // Update Group
    group
      .classed(nodeSelectors.nodePolygon, () => {
        const shape = getString(d, nodeShape, d._index)
        return shape === GraphNodeShape.Triangle || shape === GraphNodeShape.Hexagon || shape === GraphNodeShape.Square
      })

    // Update Node
    node
      .call(updateShape, nodeShape, nodeSize, d._index)
      .attr('stroke-width', getNumber(d, nodeStrokeWidth, d._index) ?? 0)
      .style('fill', getNodeColor(d, nodeFill, d._index))
      .style('stroke', getColor(d, nodeStroke, d._index, undefined, { dontFallbackToCssVar: true }) ?? null)

    const nodeBBox = (node.node() as SVGGraphicsElement).getBBox()

    nodeArc
      .attr('stroke-width', getNumber(d, nodeStrokeWidth, d._index))
      .style('display', !getNumber(d, nodeGaugeValue, d._index) ? 'none' : null)
      .style('fill', getNodeColor(d, nodeGaugeFill, d._index))
      .style('stroke', getNodeColor(d, nodeGaugeFill, d._index))
      .style('stroke-opacity', d => getString(d, nodeShape, d._index) === GraphNodeShape.Circle ? 0 : null)

    nodeArc
      .transition()
      .duration(nodeGaugeAnimDuration)
      .attrTween('d', (
        d,
        j,
        arr
      ) => {
        switch (getString(d, nodeShape, d._index)) {
          case GraphNodeShape.Circle: return arcTween(d, config, arcGenerator, arr[j])
          case GraphNodeShape.Hexagon: return polyTween(d, config, polygon, arr[j])
          case GraphNodeShape.Square: return polyTween(d, config, polygon, arr[j])
          case GraphNodeShape.Triangle: return polyTween(d, config, polygon, arr[j])
          default: return null
        }
      })

    // Set Node Selection
    updateShape(nodeSelectionOutline, nodeShape, nodeSize, d._index)

    // Update Node Icon
    const prevNodeIconValue = groupElement.nodeIcon
    const nodeIconValue = getString(d, nodeIcon, d._index)
    const nodeIconSizeValue = getNumber(d, nodeIconSize, d._index) ?? 2.5 * Math.sqrt(nodeSizeValue)
    const nodeIconColor = getNodeIconColor(d, nodeFill, d._index, selection.node())
    const shouldRenderUseElement = isInternalHref(nodeIconValue)

    if (prevNodeIconValue !== nodeIconValue) {
      // If the icon has changed, we remove all children and re-render
      icon.selectAll('*').remove()
      // If the icon is a href, we need to append a <use> element. If it's a text we append the `<text>` element.
      icon.append(shouldRenderUseElement ? 'use' : 'text')
      groupElement.nodeIcon = nodeIconValue
    }

    if (shouldRenderUseElement) {
      icon.select('use')
        .attr('href', nodeIconValue)
        .attr('x', -nodeIconSizeValue / 2)
        .attr('y', -nodeIconSizeValue / 2)
        .attr('width', nodeIconSizeValue)
        .attr('height', nodeIconSizeValue)
        .style('fill', nodeIconColor)
    } else {
      icon.select('text')
        .style('font-size', `${nodeIconSizeValue}px`)
        .attr('dy', '0.1em')
        .style('fill', nodeIconColor)
        .html(nodeIconValue)
    }

    // Side Labels
    const sideLabelsData = getValue<GraphNode<N, L>, GraphCircleLabel[]>(d, nodeSideLabels, d._index) || []
    const sideLabels = sideLabelsGroup.selectAll<SVGGElement, GraphCircleLabel>('g').data(sideLabelsData)
    const sideLabelsEnter = sideLabels.enter().append('g')
      .attr('class', nodeSelectors.sideLabelGroup)
    sideLabelsEnter.append('circle')
      .attr('class', nodeSelectors.sideLabelBackground)
      .attr('r', l => l.radius ?? SIDE_LABEL_DEFAULT_RADIUS)
    sideLabelsEnter.append('text')
      .attr('class', nodeSelectors.sideLabel)

    const sideLabelsUpdate = sideLabels.merge(sideLabelsEnter)
      .style('cursor', l => l.cursor ?? null)

    // Side label text
    sideLabelsUpdate.select(`.${nodeSelectors.sideLabel}`).html(d => d.text)
      .attr('dy', '0.1em')
      .style('fill', l => l.textColor ?? getSideLabelTextColor(l, selection.node()))
      .style('font-size', l => l.fontSize ?? `${(2 + (l.radius ?? SIDE_LABEL_DEFAULT_RADIUS)) / Math.pow(l.text.toString().length, 0.3)}px`)
      // Side label circle background
    sideLabelsUpdate.select(`.${nodeSelectors.sideLabelBackground}`)
      .style('fill', l => l.color)

    sideLabelsUpdate.attr('transform', (l, j) => {
      if (sideLabelsData.length === 1) return `translate(${nodeSizeValue / 2.5}, ${-nodeSizeValue / 2.5})`
      const r = 1.05 * nodeSizeValue / 2
      const angle = j * 1.15 * 2 * Math.atan2(l.radius ?? SIDE_LABEL_DEFAULT_RADIUS, r) - Math.PI / 3
      return `translate(${r * Math.cos(angle)}, ${r * Math.sin(angle)})`
    })

    sideLabels.exit().remove()

    // Set label and sub-label text. Expanded labels (on hover) show the full text
    // instead of the trimmed one, while wrapped labels always show the full text
    const labelFontSize = getCSSVariableValueInPixels('var(--vis-graph-node-label-font-size)', groupElement) || 12
    const subLabelFontSize = getCSSVariableValueInPixels('var(--vis-graph-node-sublabel-font-size)', groupElement) || labelFontSize
    renderNodeLabels(label, d, config, labelFontSize, subLabelFontSize)
    group
      .on('mouseenter', () => {
        renderNodeLabels(label, d, config, labelFontSize, subLabelFontSize, true)
        setLabelRect(label, nodeSelectors.labelText)
        group.raise()
      })
      .on('mouseleave', () => {
        renderNodeLabels(label, d, config, labelFontSize, subLabelFontSize)
        setLabelRect(label, nodeSelectors.labelText)
      })

    // Position label
    const labelMargin = LABEL_RECT_VERTICAL_PADDING + 1.25 * labelFontSize ** 1.03
    const nodeHeight = isStringSvg((getString(d, nodeShape, d._index)) as GraphNodeShape) ? nodeBBox.height : nodeSizeValue
    label.attr('transform', `translate(0, ${nodeHeight / 2 + labelMargin})`)
    if (scale >= ZoomLevel.Level3) setLabelRect(label, nodeSelectors.labelText)

    // Bottom Icon
    bottomIcon.html(getString(d, nodeBottomIcon, d._index))
      .attr('transform', `translate(0, ${nodeHeight / 2})`)
  })

  updateNodesPartial(selection, config, duration, scale)

  return nodeGroupsUpdate
}

export function removeNodes<N extends GraphInputNode, L extends GraphInputLink> (
  selection: Selection<SVGGElement, GraphNode<N, L>, SVGGElement, unknown>,
  config: GraphConfigInterface<N, L>,
  duration: number,
  scale = 1
): void {
  smartTransition(selection, duration / 2)
    .attr('opacity', 0)
    .attr('transform', (d, i) => {
      const configuredPosition = getValue<GraphNode<N, L>, [number, number] | undefined>(d, config.nodeExitPosition, i)
      const scale = getNumber(d, config.nodeExitScale, i) ?? 0
      const x = configuredPosition?.[0] ?? getX(d)
      const y = configuredPosition?.[1] ?? getY(d)
      return `translate(${x}, ${y}) scale(${scale})`
    })
    .remove()

  // If there's a custom render function, use it
  if (config.nodeExitCustomRenderFunction) {
    selection.each((d, i, elements) => {
      const g = select<SVGGElement, GraphNode<N, L>>(elements[i])
      config.nodeExitCustomRenderFunction(d, g, config, duration, scale)
    })
  }
}

function setLabelBackgroundRect<N extends GraphInputNode, L extends GraphInputLink> (
  selection: Selection<SVGGElement, GraphNode<N, L>, SVGGElement, unknown>
): void {
  selection.each((d, i, elements) => {
    const group: Selection<SVGGElement, N, SVGGElement, N> = select(elements[i])
    const label: Selection<SVGGElement, N, SVGGElement, N> = group.select(`.${nodeSelectors.label}`)
    setLabelRect(label, nodeSelectors.labelText)
  })
}

const setLabelBackgroundRectThrottled = throttle(setLabelBackgroundRect, 1000) as typeof setLabelBackgroundRect

export function zoomNodes<N extends GraphInputNode, L extends GraphInputLink> (
  selection: Selection<SVGGElement, GraphNode<N, L>, SVGGElement, unknown>,
  config: GraphConfigInterface<N, L>,
  scale: number
): void {
  if (config.nodeOnZoomCustomRenderFunction || config.nodeEnterCustomRenderFunction) {
    // Handle custom rendering behavior
    selection.each((d, i, elements) => {
      const g = select<SVGGElement, GraphNode<N, L>>(elements[i])
      config.nodeOnZoomCustomRenderFunction?.(d, g, config, scale)
    })
  } else {
    // Default rendering
    selection.classed(generalSelectors.zoomOutLevel1, scale < ZoomLevel.Level1)
    selection.classed(generalSelectors.zoomOutLevel2, scale < ZoomLevel.Level2)
    selection.selectAll(`${nodeSelectors.sideLabelBackground}`)
      .attr('transform', `scale(${1 / Math.pow(scale, 0.35)})`)
    selection.selectAll(`.${nodeSelectors.sideLabel}`)
      .attr('transform', `scale(${1 / Math.pow(scale, 0.45)})`)

    if (scale >= ZoomLevel.Level3) selection.call(setLabelBackgroundRectThrottled)
  }
}

export const zoomNodesThrottled = throttle(zoomNodes, 500)
